/**
 * Curve and width fitting (PLAN §2.3).
 *
 * Skeleton tracing hands us dense pixel polylines — one vertex per pixel, so a
 * 400 px stroke is 400 points. Two things have to happen before the optimizer
 * or the SVG exporter can use them:
 *
 * - **Geometry.** Schneider's least-squares cubic fit (Graphics Gems, 1990)
 *   compresses the polyline into as few cubic Bézier segments as the error
 *   budget allows. It is used instead of the paper's Dierckx B-spline fit
 *   because SVG speaks cubics natively, so nothing has to be converted on the
 *   way out, and because the optimizer's parameters are then exactly the
 *   control points the user will eventually edit.
 * - **Width.** The Euclidean distance transform of the *binarized* image gives
 *   the local half-width at every skeleton pixel almost for free. Fitting a
 *   low-degree polynomial in normalized arc length T to those samples gives the
 *   width profile w(T) the paper optimizes, and it is already close enough that
 *   M1 produces a usable vectorization with no optimization at all.
 *
 * Everything here is defensive about degenerate input: a skeleton legitimately
 * produces one-pixel specks, two-pixel stubs and paths that double back on
 * themselves, and none of those may yield NaN control points — a single NaN
 * poisons the whole GPU render.
 */

import type { PipelineParams, PixelPath, ScalarField, Stroke } from '$lib/types'
import { bernstein, pointAt, secondDerivativeAt, tangentAt } from '$lib/geometry/bezier'

/**
 * Fitted half-widths are clamped to this many pixels. A stroke narrower than a
 * quarter pixel renders as nothing at all, which the optimizer cannot recover
 * from because its gradient vanishes with its coverage.
 */
export const MIN_HALF_WIDTH = 0.25

/**
 * Ceiling on Newton-Raphson reparameterization passes before giving up and
 * splitting. Higher than the Graphics Gems value of 4 because the gain check
 * below already stops unproductive passes, so the ceiling only ever binds on
 * fits that are still improving.
 */
const MAX_REPARAM_ITERATIONS = 8

/**
 * A reparameterization pass must cut the error by at least this factor to earn
 * another one. Graphics Gems instead gates reparameterization on the initial
 * error being small, but chord-length parameterization can be badly off on a
 * perfectly fittable curve — a single cubic sampled uniformly in `t` is the
 * textbook case — so the decision is made on observed progress instead of on a
 * guess about how close the first attempt landed.
 */
const REPARAM_MIN_GAIN = 0.9

interface Vec2 {
  x: number
  y: number
}

// ---------------------------------------------------------------------------
// Bézier fitting
// ---------------------------------------------------------------------------

/**
 * Schneider's least-squares cubic Bézier fitting (Graphics Gems, 1990) with
 * recursive splitting at the point of maximum error until `maxError` is met.
 * Returns flattened control points (3n+1 points, [x0,y0,...]).
 *
 * `maxError` is a distance in pixels, not the squared quantity the original
 * Graphics Gems listing compares against.
 *
 * The recursion is run from an explicit stack: a pathological skeleton path can
 * split once per pixel, and a 5000-pixel path would blow the JS call stack.
 *
 * An empty input yields an empty array; every non-empty result has 3n+1
 * control points for n ≥ 1 segments.
 */
export function fitCurve(
  points: ArrayLike<number>,
  maxError: number,
  closed = false,
): Float32Array {
  const d = dedupe(points, closed)
  const count = d.length / 2
  if (count === 0) return new Float32Array(0)
  if (count === 1) {
    const out = new Float32Array(8)
    for (let i = 0; i < 4; i++) {
      out[i * 2] = d[0]
      out[i * 2 + 1] = d[1]
    }
    return out
  }

  const tolerance = Number.isFinite(maxError) && maxError > 0 ? maxError : 1e-3
  const last = count - 1
  const tHat1 = closed ? closingTangent(d) : normalize(d[2] - d[0], d[3] - d[1])
  const tHat2 = closed
    ? { x: -tHat1.x, y: -tHat1.y }
    : normalize(d[last * 2 - 2] - d[last * 2], d[last * 2 - 1] - d[last * 2 + 1])

  const segments: Float64Array[] = []
  const stack: Array<{ first: number; last: number; tHat1: Vec2; tHat2: Vec2 }> = [
    { first: 0, last, tHat1, tHat2 },
  ]

  while (stack.length > 0) {
    const task = stack.pop() as { first: number; last: number; tHat1: Vec2; tHat2: Vec2 }
    const nPts = task.last - task.first + 1

    if (nPts === 2) {
      segments.push(twoPointSegment(d, task.first, task.last, task.tHat1, task.tHat2))
      continue
    }

    let u = chordLengthParameterize(d, task.first, task.last)
    let bez = generateBezier(d, task.first, task.last, u, task.tHat1, task.tHat2)
    const initial = computeMaxError(d, task.first, task.last, bez, u)
    let best = bez
    let error = initial.error
    let split = initial.split

    for (let i = 0; i < MAX_REPARAM_ITERATIONS && error > tolerance; i++) {
      u = reparameterize(d, task.first, task.last, u, bez)
      bez = generateBezier(d, task.first, task.last, u, task.tHat1, task.tHat2)
      const next = computeMaxError(d, task.first, task.last, bez, u)
      const previous = error
      // Newton can overshoot, so the best fit seen is what gets kept.
      if (next.error < error) {
        error = next.error
        split = next.split
        best = bez
      }
      if (!(next.error < previous * REPARAM_MIN_GAIN)) break
    }

    if (error <= tolerance) {
      segments.push(best)
      continue
    }

    // Split at the worst point, always making progress even when the error
    // maximum lands on an endpoint.
    let splitIndex = split
    if (splitIndex <= task.first || splitIndex >= task.last) {
      splitIndex = (task.first + task.last) >> 1
    }
    const center = centerTangent(d, splitIndex)
    // Pushed right-first so the left half is processed first and segments come
    // out in path order.
    stack.push({
      first: splitIndex,
      last: task.last,
      tHat1: { x: -center.x, y: -center.y },
      tHat2: task.tHat2,
    })
    stack.push({ first: task.first, last: splitIndex, tHat1: task.tHat1, tHat2: center })
  }

  const out = new Float32Array((segments.length * 3 + 1) * 2)
  out[0] = segments[0][0]
  out[1] = segments[0][1]
  for (let s = 0; s < segments.length; s++) {
    const bez = segments[s]
    for (let i = 1; i < 4; i++) {
      out[(s * 3 + i) * 2] = bez[i * 2]
      out[(s * 3 + i) * 2 + 1] = bez[i * 2 + 1]
    }
  }
  return out
}

/**
 * Drops consecutive duplicate points. Zero-length chords make the chord-length
 * parameterization and every tangent estimate divide by zero, and skeleton
 * walks routinely revisit a pixel (for example at a resolved junction copy).
 */
function dedupe(points: ArrayLike<number>, closed: boolean): Float64Array {
  const count = Math.floor(points.length / 2)
  const kept: number[] = []
  for (let i = 0; i < count; i++) {
    const x = points[i * 2]
    const y = points[i * 2 + 1]
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue
    const n = kept.length
    if (n >= 2 && kept[n - 2] === x && kept[n - 1] === y) continue
    kept.push(x, y)
  }
  if (closed && kept.length >= 4) {
    const n = kept.length
    if (kept[0] !== kept[n - 2] || kept[1] !== kept[n - 1]) kept.push(kept[0], kept[1])
  }
  return Float64Array.from(kept)
}

/**
 * Tangent at the seam of a closed path, taken across the join so the first and
 * last segments meet with G¹ continuity instead of a corner.
 */
function closingTangent(d: Float64Array): Vec2 {
  const count = d.length / 2
  const prev = count - 2
  const t = normalize(d[2] - d[prev * 2], d[3] - d[prev * 2 + 1])
  if (t.x !== 0 || t.y !== 0) return t
  return normalize(d[2] - d[0], d[3] - d[1])
}

function twoPointSegment(
  d: Float64Array,
  first: number,
  last: number,
  tHat1: Vec2,
  tHat2: Vec2,
): Float64Array {
  const x0 = d[first * 2]
  const y0 = d[first * 2 + 1]
  const x3 = d[last * 2]
  const y3 = d[last * 2 + 1]
  const dist = Math.hypot(x3 - x0, y3 - y0) / 3
  return Float64Array.from([
    x0,
    y0,
    x0 + tHat1.x * dist,
    y0 + tHat1.y * dist,
    x3 + tHat2.x * dist,
    y3 + tHat2.y * dist,
    x3,
    y3,
  ])
}

function chordLengthParameterize(d: Float64Array, first: number, last: number): Float64Array {
  const n = last - first + 1
  const u = new Float64Array(n)
  for (let i = 1; i < n; i++) {
    u[i] =
      u[i - 1] +
      Math.hypot(
        d[(first + i) * 2] - d[(first + i - 1) * 2],
        d[(first + i) * 2 + 1] - d[(first + i - 1) * 2 + 1],
      )
  }
  const total = u[n - 1]
  if (total > 0) {
    for (let i = 1; i < n; i++) u[i] /= total
  } else {
    for (let i = 0; i < n; i++) u[i] = i / (n - 1)
  }
  u[n - 1] = 1
  return u
}

/**
 * Solves for the two interior control points given fixed endpoints and end
 * tangents — the least-squares core of Schneider's method.
 */
function generateBezier(
  d: Float64Array,
  first: number,
  last: number,
  u: Float64Array,
  tHat1: Vec2,
  tHat2: Vec2,
): Float64Array {
  const n = last - first + 1
  const x0 = d[first * 2]
  const y0 = d[first * 2 + 1]
  const x3 = d[last * 2]
  const y3 = d[last * 2 + 1]

  let c00 = 0
  let c01 = 0
  let c11 = 0
  let r0 = 0
  let r1 = 0
  for (let i = 0; i < n; i++) {
    const b = bernstein(u[i])
    const a0x = tHat1.x * b[1]
    const a0y = tHat1.y * b[1]
    const a1x = tHat2.x * b[2]
    const a1y = tHat2.y * b[2]
    c00 += a0x * a0x + a0y * a0y
    c01 += a0x * a1x + a0y * a1y
    c11 += a1x * a1x + a1y * a1y
    const tx = d[(first + i) * 2] - (x0 * (b[0] + b[1]) + x3 * (b[2] + b[3]))
    const ty = d[(first + i) * 2 + 1] - (y0 * (b[0] + b[1]) + y3 * (b[2] + b[3]))
    r0 += a0x * tx + a0y * ty
    r1 += a1x * tx + a1y * ty
  }

  const det = c00 * c11 - c01 * c01
  let alphaL = 0
  let alphaR = 0
  if (Math.abs(det) > 1e-12) {
    alphaL = (r0 * c11 - c01 * r1) / det
    alphaR = (c00 * r1 - r0 * c01) / det
  }

  // Wu/Barsky heuristic: a negative or vanishing alpha means the least-squares
  // solution folded the curve back on itself, so fall back to the chord/3 rule.
  const chord = Math.hypot(x3 - x0, y3 - y0)
  const epsilon = 1e-6 * chord
  if (!(alphaL > epsilon) || !(alphaR > epsilon)) {
    alphaL = chord / 3
    alphaR = chord / 3
  }

  return Float64Array.from([
    x0,
    y0,
    x0 + tHat1.x * alphaL,
    y0 + tHat1.y * alphaL,
    x3 + tHat2.x * alphaR,
    y3 + tHat2.y * alphaR,
    x3,
    y3,
  ])
}

function computeMaxError(
  d: Float64Array,
  first: number,
  last: number,
  bez: Float64Array,
  u: Float64Array,
): { error: number; split: number } {
  let error = 0
  let split = (first + last) >> 1
  const p = { x: 0, y: 0 }
  for (let i = 1; i < last - first; i++) {
    pointAt(bez, 0, u[i], p)
    const dist = Math.hypot(p.x - d[(first + i) * 2], p.y - d[(first + i) * 2 + 1])
    if (dist >= error) {
      error = dist
      split = first + i
    }
  }
  return { error, split }
}

function reparameterize(
  d: Float64Array,
  first: number,
  last: number,
  u: Float64Array,
  bez: Float64Array,
): Float64Array {
  const n = last - first + 1
  const out = new Float64Array(n)
  for (let i = 0; i < n; i++) {
    out[i] = newtonRaphson(bez, d[(first + i) * 2], d[(first + i) * 2 + 1], u[i])
  }
  out[0] = 0
  out[n - 1] = 1
  // Newton steps can cross, which breaks the monotone parameterization the
  // least-squares fit assumes; nudging keeps the order without another solve.
  for (let i = 1; i < n; i++) {
    if (out[i] <= out[i - 1]) out[i] = Math.min(1, out[i - 1] + 1e-6)
  }
  return out
}

function newtonRaphson(bez: Float64Array, px: number, py: number, u: number): number {
  const q = pointAt(bez, 0, u)
  const q1 = tangentAt(bez, 0, u)
  const q2 = secondDerivativeAt(bez, 0, u)
  const dx = q.x - px
  const dy = q.y - py
  const numerator = dx * q1.x + dy * q1.y
  const denominator = q1.x * q1.x + q1.y * q1.y + dx * q2.x + dy * q2.y
  if (Math.abs(denominator) < 1e-12) return u
  const next = u - numerator / denominator
  if (!Number.isFinite(next)) return u
  return Math.min(1, Math.max(0, next))
}

/** Backward-pointing tangent at an interior point, used on both halves of a split. */
function centerTangent(d: Float64Array, index: number): Vec2 {
  const v1 = normalize(d[index * 2 - 2] - d[index * 2], d[index * 2 - 1] - d[index * 2 + 1])
  const v2 = normalize(d[index * 2] - d[index * 2 + 2], d[index * 2 + 1] - d[index * 2 + 3])
  const t = normalize(v1.x + v2.x, v1.y + v2.y)
  if (t.x !== 0 || t.y !== 0) return t
  return v1.x !== 0 || v1.y !== 0 ? v1 : { x: 1, y: 0 }
}

function normalize(x: number, y: number): Vec2 {
  const length = Math.hypot(x, y)
  if (!(length > 0) || !Number.isFinite(length)) return { x: 0, y: 0 }
  return { x: x / length, y: y / length }
}

// ---------------------------------------------------------------------------
// Width fitting
// ---------------------------------------------------------------------------

/**
 * Least-squares fit of the half-width polynomial w(T) = Σ a_k T^k against the
 * distance transform sampled along the path. Returns `degree + 1` coefficients.
 * Degree 0 yields the mean.
 *
 * The system is solved by Householder QR rather than the normal equations: the
 * design matrix is a Vandermonde in T ∈ [0,1], whose condition number squares
 * under AᵀA and goes visibly wrong by the degree 8 the paper uses for textured
 * art. A rank-deficient or non-finite solve falls back to the mean, which is
 * always a valid width profile.
 */
export function fitWidth(path: PixelPath, edt: ScalarField, degree: number): Float32Array {
  const requested = Number.isFinite(degree) ? Math.max(0, Math.floor(degree)) : 0
  const out = new Float32Array(requested + 1)
  const count = path.points.length / 2
  if (count === 0) return out

  const w = new Float64Array(count)
  const t = new Float64Array(count)
  let total = 0
  for (let i = 0; i < count; i++) {
    w[i] = sampleBilinear(edt, path.points[i * 2], path.points[i * 2 + 1])
    if (i > 0) {
      total += Math.hypot(
        path.points[i * 2] - path.points[i * 2 - 2],
        path.points[i * 2 + 1] - path.points[i * 2 - 1],
      )
    }
    t[i] = total
  }
  if (total > 0) {
    for (let i = 0; i < count; i++) t[i] /= total
  } else {
    for (let i = 0; i < count; i++) t[i] = count > 1 ? i / (count - 1) : 0
  }

  let mean = 0
  for (let i = 0; i < count; i++) mean += w[i]
  mean /= count

  const effective = Math.min(requested, count - 1)
  if (effective < 1) {
    out[0] = mean
    return out
  }

  const columns = effective + 1
  const a = new Float64Array(count * columns)
  for (let i = 0; i < count; i++) {
    let power = 1
    for (let k = 0; k < columns; k++) {
      a[i * columns + k] = power
      power *= t[i]
    }
  }
  const solution = solveLeastSquares(a, w, count, columns)
  if (!solution) {
    out[0] = mean
    return out
  }
  for (let k = 0; k < columns; k++) out[k] = solution[k]
  return out
}

/** Bilinear sample of a scalar field, clamped at the borders. */
export function sampleBilinear(field: ScalarField, x: number, y: number): number {
  const { width, height, data } = field
  if (width <= 0 || height <= 0) return 0
  const cx = Math.min(width - 1, Math.max(0, Number.isFinite(x) ? x : 0))
  const cy = Math.min(height - 1, Math.max(0, Number.isFinite(y) ? y : 0))
  const x0 = Math.floor(cx)
  const y0 = Math.floor(cy)
  const x1 = Math.min(width - 1, x0 + 1)
  const y1 = Math.min(height - 1, y0 + 1)
  const fx = cx - x0
  const fy = cy - y0
  const v00 = data[y0 * width + x0]
  const v10 = data[y0 * width + x1]
  const v01 = data[y1 * width + x0]
  const v11 = data[y1 * width + x1]
  return (
    v00 * (1 - fx) * (1 - fy) + v10 * fx * (1 - fy) + v01 * (1 - fx) * fy + v11 * fx * fy
  )
}

/**
 * Least-squares solve of `A x ≈ b` (A is `m × n`, row-major) by Householder QR.
 * Returns `null` when the system is rank deficient or the result is not finite.
 */
function solveLeastSquares(
  a: Float64Array,
  b: Float64Array,
  m: number,
  n: number,
): Float64Array | null {
  if (m < n || n === 0) return null
  const r = Float64Array.from(a)
  const y = Float64Array.from(b)
  const v = new Float64Array(m)

  for (let k = 0; k < n; k++) {
    let norm = 0
    for (let i = k; i < m; i++) norm += r[i * n + k] * r[i * n + k]
    norm = Math.sqrt(norm)
    if (!(norm > 1e-12)) return null

    const alpha = r[k * n + k] > 0 ? -norm : norm
    let vNormSq = 0
    for (let i = k; i < m; i++) {
      v[i] = r[i * n + k]
    }
    v[k] -= alpha
    for (let i = k; i < m; i++) vNormSq += v[i] * v[i]
    if (!(vNormSq > 0)) continue

    for (let j = k; j < n; j++) {
      let s = 0
      for (let i = k; i < m; i++) s += v[i] * r[i * n + j]
      s = (2 * s) / vNormSq
      for (let i = k; i < m; i++) r[i * n + j] -= s * v[i]
    }
    let s = 0
    for (let i = k; i < m; i++) s += v[i] * y[i]
    s = (2 * s) / vNormSq
    for (let i = k; i < m; i++) y[i] -= s * v[i]
  }

  const x = new Float64Array(n)
  for (let i = n - 1; i >= 0; i--) {
    let sum = y[i]
    for (let j = i + 1; j < n; j++) sum -= r[i * n + j] * x[j]
    const diagonal = r[i * n + i]
    if (Math.abs(diagonal) < 1e-12) return null
    x[i] = sum / diagonal
  }
  for (let i = 0; i < n; i++) if (!Number.isFinite(x[i])) return null
  return x
}

// ---------------------------------------------------------------------------
// Strokes
// ---------------------------------------------------------------------------

export function fitStroke(path: PixelPath, edt: ScalarField, params: PipelineParams): Stroke {
  const degree =
    params.widthMode === 'uniform'
      ? 0
      : Number.isFinite(params.widthDegree)
        ? Math.max(0, Math.floor(params.widthDegree))
        : 0
  const points = fitCurve(path.points, params.fitError, path.closed)
  const width = clampWidthProfile(fitWidth(path, edt, degree))
  return { points, width, closed: path.closed }
}

export function fitStrokes(paths: PixelPath[], edt: ScalarField, params: PipelineParams): Stroke[] {
  const strokes: Stroke[] = []
  for (const path of paths) {
    const stroke = fitStroke(path, edt, params)
    if (stroke.points.length >= 8) strokes.push(stroke)
  }
  return strokes
}

/**
 * Lifts a width profile so it never dips below {@link MIN_HALF_WIDTH}.
 *
 * The shift is applied to the constant term only, so the *shape* of a taper
 * survives; scaling the whole polynomial would flatten exactly the variation
 * the variable-width mode exists to capture.
 */
function clampWidthProfile(coefficients: Float32Array): Float32Array {
  for (let i = 0; i < coefficients.length; i++) {
    if (!Number.isFinite(coefficients[i])) {
      const fallback = new Float32Array(coefficients.length)
      fallback[0] = MIN_HALF_WIDTH
      return fallback
    }
  }
  const samples = 33
  let min = Infinity
  for (let i = 0; i < samples; i++) {
    const t = i / (samples - 1)
    let value = 0
    for (let k = coefficients.length - 1; k >= 0; k--) value = value * t + coefficients[k]
    if (value < min) min = value
  }
  if (min < MIN_HALF_WIDTH) coefficients[0] += MIN_HALF_WIDTH - min
  return coefficients
}
